import {DownloadOutlined} from '@ant-design/icons';
import {Button, Dropdown, Menu, Modal, Pagination, Select, Space, Table, Tag, Tooltip} from 'antd';
import {format, parseISO} from 'date-fns';
import {useEffect, useRef, useState} from 'react';
import {toast} from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {RQGetAllUserOrders, RQGetOrderByFilter, RqUpdateOrder} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';
import {InvoiceScreen} from '../../Order/InvoiceScreen';

const expandColumns = [
  {
    title: 'Product Image',
    dataIndex: 'imageUrl',
    key: 'imageUrl',
    render: url =>
      url ? (
        <img
          src={url}
          alt={'Product'}
          style={{
            width: 60,
            height: 60,
            objectFit: 'cover',
            borderRadius: 4,
            border: '1px solid #ddd',
          }}
        />
      ) : (
        <Tag color={'default'}>No Image</Tag>
      ),
  },
  {title: 'Product Name', dataIndex: 'productName', key: 'productName'},
  {title: 'Product Type', dataIndex: 'productType', key: 'productType'},
  {title: 'Jewelry Type', dataIndex: 'jewelryType', key: 'jewelryType'},
  {title: 'Price Per Unit', dataIndex: 'pricePerUnit', key: 'pricePerUnit'},
  {
    title: 'Weight',
    dataIndex: 'weight',
    key: 'weight',
  },
  {
    title: 'Size & Qty',
    key: 'sizes',
    dataIndex: 'size',
    render: sizeArray =>
      Array.isArray(sizeArray) ? (
        <Space direction={'vertical'}>
          {sizeArray.map((item, index) => (
            <Tag key={index} color={'blue'}>
                    Size: {item.size}, Qty: {item.quantity}
            </Tag>
          ))}
        </Space>
      ) : (
        'N/A'
      ),
  },
  {
    title: 'Gift',
    dataIndex: 'gift',
    key: 'gift',
    render: gift =>
      gift ? (
        <p>🎁</p>
      ) : (
        <p>-</p>
      ),
  },
];

const UserHistory = () => {
  const [userOrders, setUserOrders] = useState([]);

  const [products, setProducts] = useState(null);

  const [dataSource, setDataSource] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedUser, setSelectedUser] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState(null);

  const [pageSize, setPageSize] = useState(10);

  const [editingCell, setEditingCell] = useState({key: null, field: null});

  const [editedStatus, setEditedStatus] = useState(null);

  const [selectedOrderSummary, setSelectedOrderSummary] = useState(null);

  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const {data: userData} = RQGetAllUserOrders(token);

  const {Option} = Select;

  const data = dataSource;

  const pageSizeOptions = ['10', '20', '30', '50'];

  const updateOrderMutation = RqUpdateOrder();

  const uniqueUsers = userData
    ? [...new Map(userData.map(user => [user.userInfo?.id, user.userInfo])).values()]
    : [];

  const uniqueProducts = userData
    ? [...new Map(
      userData
        .flatMap(order => order.products || [])  
        .map(p => [p?.productId, p]),              
    ).values()]
    : [];

  const filterConfig = {};
  if (selectedUser) { filterConfig.userId = selectedUser; }
  if (products) { filterConfig.productId = products; }
  if(selectedStatus) {filterConfig.status = selectedStatus;}

  const {data: filterdOrderData} = RQGetOrderByFilter( token, filterConfig);

  const isFilterApplied = selectedUser || products || selectedStatus;

  const tableData = isFilterApplied
    ? (filterdOrderData || [])
    : dataSource;

  const paginatedData = tableData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handleResetFilters = () =>{
    setSelectedUser(null);
    setProducts(null);
    setSelectedStatus(null);
  };
  const handleSave = async (id, field, newValue) => {
    try {
      await updateOrderMutation.mutateAsync({
        token,
        formData: {[field]: newValue}, 
        id,
      });

      toast.success('Order Status updated successfully');

      setDataSource(prev =>
        prev.map(row =>
          row.id === id ? {...row, [field]: newValue} : row,
        ),
      );
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const inputRef = useRef(null);

  useEffect(() => {
    if (userData) {
      const updated = userData
        .map(order => ({
          ...order,
          totalQuantity: (order.products || []).length,
          key: order.id,
        }))
        .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));

      setUserOrders(updated);
      setDataSource(updated);
    }
  }, [userData]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingCell]);

  const expandedRowRender = record => {
    const products = (record.products || []).map((product, index) => {
      const sizeArray = product.size?.length
        ? product.size
        : product.productInfo?.size || [];

      return {
        key: `${record.id}-${product.productId}`,
        imageUrl: product.productInfo?.imageUrl?.[0] || '',
        productName: product.productName || 'Unknown',
        productType: product.productType || 'Unknown',
        jewelryType: product.jewelryType || 'Unknown',
        pricePerUnit: product.pricePerUnit || 'No',
        size: sizeArray,
        weight:product.weight || 'No',
        gift: String(product.gift).toLowerCase() === 'true',
        transactionId : product?.transactionId || '-',
      };
    });

    return (
      <Table
        columns={expandColumns}
        dataSource={products}
        pagination={false}
        rowKey={'key'}
      />
    );
  };

  const columns = [
    {
      title: 'No.',
      key: 'index',
      width: 30,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'User',
      key: 'user',
      width: 100,
      render: (_, record) => {
        const user = record.userInfo;
        return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
      },
    },
    {title: 'Transaction Id', dataIndex: 'transactionId', key: 'transactionId', width: 100},
    {title: 'Total Amount', dataIndex: 'totalAmount', key: 'totalAmount', width: 100},
    {title: 'Total Qty', dataIndex: 'totalQuantity', key: 'totalQuantity', width: 100},
  
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (text, record) => {
        const handleStatusChange = ({key}) => {
          const newStatus = key;
          if (newStatus !== record.status) {
            handleSave(record.id, 'status', newStatus);
          }
        };

        return (
          <Dropdown
            overlay={(
              <Menu onClick={handleStatusChange}>
                {record.status !== 'CONFIRMED' && (
                  <Menu.Item key={'CONFIRMED'}>
                    <Tag color={'green'}>CONFIRMED</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'PENDING' && (
                  <Menu.Item key={'PENDING'}>
                    <Tag color={'orange'}>PENDING</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'CANCELLED' && (
                  <Menu.Item key={'CANCELLED'}>
                    <Tag color={'red'}>CANCELLED</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'SHIPPED' && (
                  <Menu.Item key={'SHIPPED'}>
                    <Tag color={'blue'}>SHIPPED</Tag>
                  </Menu.Item>
                )}
                {record.status !== 'DELIVERED' && (
                  <Menu.Item key={'DELIVERED'}>
                    <Tag color={'purple'}>DELIVERED</Tag>
                  </Menu.Item>
                )}
              </Menu>
            )}
            trigger={['click']}
          >
            <div className={'cursor-pointer w-fit'}>
              <Tag
                color={
                  text === 'CONFIRMED'
                    ? 'green'
                    : text === 'PENDING'
                      ? 'orange'
                      : text === 'CANCELLED'
                        ? 'red'
                        : text === 'SHIPPED'
                          ? 'blue'
                          : text === 'DELIVERED'
                            ? 'purple'
                            : 'default'
                }
              >
                {text} <span> &#9662;</span>
              </Tag>
            </div>
          </Dropdown>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 100,
      render: date => format(parseISO(date), 'dd/MM/yyyy HH:mm'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title={'Show Invoice'}>
          <Button
            type={'link'}
            icon={<DownloadOutlined />}
            onClick={() => {
              setOpenInvoiceModal(true);
              setSelectedOrderSummary(record);
            }
            }
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div className={'flex flex-wrap gap-2 items-center mt-3 w-full mb-5'}>
        <Select 
          placeholder={'Select User'}
          className={'w-48'}
          allowClear
          value={selectedUser}
          onChange={value => setSelectedUser(value)} 
        >
          {uniqueUsers.filter(Boolean).map(user => (
            <Option key={user?.id} value={user?.id}>
              {user?.firstName} {user?.lastName}
            </Option>
          ))}
        </Select>
        <Select 
          placeholder={'Select Product'}
          className={'w-48'}
          allowClear
          value={products}
          onChange={value => setProducts(value)} 
        >
          {uniqueProducts.filter(Boolean).map(product => (
            <Option key={product?.productId} value={product?.productId}>
              {product?.productName}
            </Option>
          ))}
        </Select>
        <Select 
          placeholder={'Select Status'}
          className={'w-48'}
          allowClear
          value={selectedStatus}
          onChange={value => setSelectedStatus(value)} 
        >
          <Option key={'CONFIRMED'} value={'CONFIRMED'}>Confirmed</Option>
          <Option key={'PENDING'} value={'PENDING'}>Pending</Option>
          <Option key={'CANCELLED'} value={'CANCELLED'}>Cancelled</Option>
          <Option key={'SHIPPED'} value={'SHIPPED'}>Shipped</Option>
          <Option key={'DELIVERED'} value={'DELIVERED'}>Delivered</Option>

        </Select>
        <Button className={'w-24 !bg-primary-900 !text-white'} type={'primary'} onClick={handleResetFilters}>Reset</Button>
      </div>
      <Table
        columns={columns}
        expandable={{expandedRowRender, expandIconColumnIndex: 1}}
        dataSource={paginatedData}
        size={'middle'}
        scroll={{x: 'max-content'}}
        rowKey={row => row.id}
        pagination={false}
      />
      <div className={'flex justify-between items-center mt-4 px-2'}>
        <Select
          value={String(pageSize)}
          onChange={value => {
            setPageSize(Number(value));
            setCurrentPage(1);
          }}
          style={{width: 120}}
        >
          {pageSizeOptions.map(size => (
            <Option key={size} value={size}>
              {size} / page
            </Option>
          ))}
        </Select>

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={tableData.length}
          showSizeChanger={false}
          onChange={page => setCurrentPage(page)}
        />
        <Modal
          title={'Invoice'}
          open={openInvoiceModal}
          width={600}
          closable
          centered
          footer={null}
          onCancel={() => setOpenInvoiceModal(false)}
        >
          <InvoiceScreen
            setOpenInvoiceModal={setOpenInvoiceModal}
            selectedOrderSummary={selectedOrderSummary}
            setSelectedOrderSummary={setSelectedOrderSummary}
          />
        </Modal>
      </div>
    </div>
  )
  ;
};

export default UserHistory;
